import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router, UrlSegment } from '@angular/router';
import { filter } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { TitleCasePipe } from '@angular/common';
import { LoggerService } from '@shared/services/logger.service';
import { removeQueryParams } from '@shared/utils/url.utils';
import { Store } from '@ngrx/store';
import * as _ from 'lodash';
import {ApplicationState} from "@app-store";

@Injectable({
  providedIn: 'root'
})
export class BreadcrumbService {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private titleCasePipe = inject(TitleCasePipe);
  private logger = inject(LoggerService);

  protected store: Store<ApplicationState> = inject(Store);

  home: MenuItem = { icon: 'pi pi-home', routerLink: [''] };

  breadcrumbs: MenuItem[] = [];

  segmentVerboseMap: any = {
    'administration': $localize`amministrazione`,
    'clients': $localize`clienti`,
    'hardware': $localize`dispositivi`,
    'profile': $localize`profilo`,
    'users': $localize`utenti`,
    'doctors': $localize`dottori`,
    'members': $localize`pazienti`,
    'components': $localize`componenti`,
    'locations': $localize`località`,
    'contracts': $localize`contratti`,
    'orders': $localize`ordini`,
    'products': $localize`prodotti`,
    'permissions': $localize`permessi`,
    'authorities': $localize`ruoli`,
    'exercises': $localize`esercizi`,
    'tags': $localize`tag`,
    'new': $localize`nuovo`,
  };

  constructor() {
    this.logger.debug(this.name, 'constructor');
    // prefill Breadcrumbs at creation
    if (this.breadcrumbs.length === 0) {
      this.generate(this.router.url);
    }
    // handle Breadcrumbs on route changes
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe({
      next: (event) => {
        this.breadcrumbs = [];
        this.generate((event as NavigationEnd).urlAfterRedirects);
      },
      error: (error) => this.logger.error(this.name, 'Error', error),
    });
  }

  get name() {
    return this.constructor.name;
  }

  // Parses the NavigationEnd event to construct breadcrumb data based on the route hierarchy and snapshots available in the provided URL.
  private generate(url: string): void {
    const paths: string[] = this.getSnapshotPaths(url);
    const configs: any[] = this.getSnapshotConfigurations(paths);
    paths.forEach((path, index) => {
      const config = configs[index];
      if (config) {
        this.breadcrumbs.push({
          id: `${index}-${path}`,
          label: this.titleCasePipe.transform(config.label),
          visible: config.visible,
          disabled: index === paths.length -1 || config.disabled,
          routerLink: config.routerLink || paths.slice(1, index + 1),
          skipLocationChange: false
        });
      }
    })
    this.logger.debug(this.name, paths, configs);
  }

  /*
   * Cleans the provided URL by removing query parameters, parsing its path components, and combining them with route snapshot paths.
   * Filters and returns a unique list of paths.
   *
   * @param {string} url - The URL to be cleaned and processed.
   * @return {string[]} A unique array of path components derived from the cleaned URL and route snapshots.
   */
  private getSnapshotPaths(url: string) {
    const cleaned: string = removeQueryParams(url);
    const urlPaths: string[] = cleaned.replace('/app', 'app').split('/');
    let snapshotPaths: string[] = [], route = this.route, index = 0;
    while (route.firstChild) {
      route = route.firstChild;
      const url = this.getRouteSnapshotUrl(route);
      if (url.length !== 0) snapshotPaths.push(url.toString());
      index++;
    }
    snapshotPaths = snapshotPaths.filter(path => path !== '');
    return _.uniq([...urlPaths, ...snapshotPaths]);
  }


  /*
   * Retrieves an array of snapshot configuration data based on the route's breadcrumb structure.
   * It traverses through each child route starting from the current route, collecting breadcrumb configurations.
   *
   * @return {any[]} An array of breadcrumb configuration strings, collected from the route data.
   */
  private getSnapshotConfigurations(paths: string[]) {
    let configs: any[] = [], route = this.route, index = 0;
    while (route.firstChild) {
      route = route.firstChild;
      const url = this.getRouteSnapshotUrl(route);
      const data = this.getRouteSnapshotData(route);
      const {breadcrumbs} = data ?? null;
      if (paths.includes(url.toString())) (!configs.includes(breadcrumbs) && breadcrumbs) ? configs.push(breadcrumbs): configs.push(null);
      index++;
    }
    return configs;
  }

  private getBreadcrumbVisible(
    snapshot: ActivatedRouteSnapshot,
  ): boolean {
    const {breadcrumbs} = snapshot.data ?? null;
    if (breadcrumbs && (typeof breadcrumbs === 'object') && 'visible' in breadcrumbs) {
      return breadcrumbs.visible;
    }
    return false;
  }

  private getBreadcrumbDisabled(
    snapshot: ActivatedRouteSnapshot,
  ): boolean {
    const {breadcrumbs} = snapshot.data ?? null;
    if (breadcrumbs && (typeof breadcrumbs === 'object') && 'disabled' in breadcrumbs) {
      return breadcrumbs.disabled;
    }
    return false;
  }

  private getRouteSnapshotData(route: ActivatedRoute) {
    const snapshot = route.snapshot;
    const data = snapshot ? snapshot.data : {};
    this.logger.debug(this.name, 'Snapshot data:', data);
    return data;
  }

  private getRouteSnapshotUrl(route: ActivatedRoute): UrlSegment[] {
    const snapshot = route.snapshot;
    const url = snapshot ? snapshot.url : [];
    this.logger.debug(this.name, 'Snapshot url:', url);
    return url;
  }

}
